/**
 * Natural-language labels for all EventTypes.
 * Shared across history UI components (timeline, full-event list).
 */

import { EventLogEntry, EventType } from '~/server/src/dtos';

export type TranslateFn = (
  key: string,
  opts?: { placeholders?: Record<string, string | number> },
) => string;

/**
 * Maps EventType to translation key.
 * Covers all event types with natural-language labels.
 */
export const eventTypeToLabelKey: Partial<Record<EventType, string>> = {
  [EventType.STARTGAME]: 'event_game_started',
  [EventType.STARTROUND]: 'event_round_started',
  [EventType.ENDROUND]: 'event_round_ended',
  [EventType.ENDGAME]: 'event_game_ended',
  [EventType.MERGECARDS]: 'event_merge_cards',
  [EventType.MERGECARDSOPP]: 'event_merge_cards_opp',
  [EventType.ADDBONUSCARDS]: 'event_bonus_cards',
  [EventType.ADDBONUSCARDSOPP]: 'event_bonus_cards_opp',
  [EventType.SKIPPLAYER]: 'event_player_skipped',
  [EventType.JOINGAME]: 'event_player_joined',
  [EventType.JOINGAMEOPP]: 'event_player_joined',
  [EventType.NEWGAME]: 'event_game_created',
  [EventType.TM_GAMECREATED]: 'event_game_created',
  [EventType.LEAVEGAMEOPP]: 'event_player_left',
  [EventType.REJOINGAME]: 'event_player_rejoined',
  [EventType.TM_REJOINGAME]: 'event_player_rejoined',
  [EventType.SELECTMOVEOPP]: 'event_move_opp',
  [EventType.SELECTDICESOPP]: 'event_dice_selected_opp',
  [EventType.SELECTDICES]: 'event_dice_rolled',
  [EventType.ENDTURN]: 'event_turn_ended',
  [EventType.SELECTCARDDICE]: 'event_card_dice_selection',
  [EventType.STARTTURN]: 'event_turn_started',
};

/** State players at event time (may have userid/userId/name) for resolving player from payload.playerIndex when senderId is missing. */
export type StatePlayers = Array<{ userid?: string; userId?: string; name?: string }>;

/**
 * Gets a natural-language label for an event log entry.
 * Falls back to the raw EventType if no translation key exists.
 * @param entry - The event log entry
 * @param t - Translation function
 * @param players - Optional array of players to resolve senderId to player names
 * @param horses - Optional array of horses to resolve horseId to horse names
 * @param statePlayers - Optional game-state players at event time; used with payload.playerIndex or statePlayerIndex when senderId is missing
 * @param statePlayerIndex - Optional player index (into statePlayers) for the acting player, e.g. from turnorder[turnpos] -> horses[].playerid
 */
export function getEventLabel(
  entry: EventLogEntry,
  t: TranslateFn,
  players?: Array<{ userId?: string; name?: string }>,
  horses?: Array<{ id?: string | number; name?: string }>,
  statePlayers?: StatePlayers,
  statePlayerIndex?: number,
): string {
  const key = eventTypeToLabelKey[entry.eventType];
  if (!key) {
    return entry.eventType;
  }

  // Get the base translation string to check which placeholders it needs
  const baseTranslation = t(key);
  // Collect all applicable placeholders (round, player, horse) together
  const placeholders: Record<string, string | number> = {};

  // Check if translation needs round placeholder
  if (baseTranslation.includes('{{round}}')) {
    if (entry.payload?.round !== undefined) {
      placeholders.round = (entry.payload.round as number) + 1;
    }
  }

  // Check if translation needs player placeholder
  if (baseTranslation.includes('{{player}}')) {
    // Try to resolve player name from senderId (support both userId and userid on player objects)
    if (entry.senderId && players) {
      const player = players.find((p) => {
        const id =
          (p as { userId?: string; userid?: string }).userid ??
          (p as { userId?: string; userid?: string }).userId;
        return id === entry.senderId;
      });
      if (player?.name) {
        placeholders.player = player.name;
      }
    }
    // Fallback: resolve from statePlayerIndex (e.g. from turnorder[turnpos] -> horses[].playerid) or payload.playerIndex + statePlayers
    if (!placeholders.player && statePlayers?.length) {
      const payload = entry.payload as Record<string, unknown> | undefined;
      const playerIndex =
        typeof statePlayerIndex === 'number'
          ? statePlayerIndex
          : ((payload?.playerIndex as number | undefined) ??
            (payload?.playerid as number | undefined));
      if (typeof playerIndex === 'number') {
        const player = statePlayers[playerIndex];
        const playerName = player?.name;
        if (playerName) {
          placeholders.player = playerName;
        }
      }
    }
    // Fallback to payload.playerName if available
    if (!placeholders.player && entry.payload?.playerName) {
      placeholders.player = entry.payload.playerName as string;
    }
    // Final fallback for unresolved player
    if (!placeholders.player) {
      placeholders.player = t('history_unknown_player');
    }
  }

  // Check if translation needs horse placeholder
  if (baseTranslation.includes('{{horse}}')) {
    let horseName: string | undefined;

    // First, try to get horse name directly from payload
    if (entry.payload?.horseName) {
      horseName = entry.payload.horseName as string;
    } else if (entry.payload?.horse && typeof entry.payload.horse === 'string') {
      horseName = entry.payload.horse;
    }

    // If not found, try to look up by horseId from horses array
    if (!horseName && entry.payload?.horseId && horses) {
      const horseId = entry.payload.horseId;
      const horse = horses.find((h) => {
        if (h.id !== undefined) {
          return String(h.id) === String(horseId) || h.id === horseId;
        }
        return false;
      });
      if (horse?.name) {
        horseName = horse.name;
      }
    }

    // If still not found, use fallback
    if (!horseName) {
      horseName = t('history_unknown_horse');
    }

    placeholders.horse = horseName;
  }

  // If we have any placeholders, use them; otherwise return base translation
  if (Object.keys(placeholders).length > 0) {
    return t(key, { placeholders });
  }

  return baseTranslation;
}
